<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div class="device-icon-pin" :class="[sizeClass, isOn ? 'device-active--fan' : '']" :title="device.deviceName">
      <!-- The drawn fan inside the status ring (P13); it spins at the speed it
           reports while it runs, and the ring carries that speed. -->
      <ControlStatusRing :value="ringValue" accent="var(--device-color-fan)">
        <div class="device-icon-pin__icon" :class="{ 'device-icon-pin__icon--active': isOn }">
          <!-- A pump is a motor too (the hub's Pump class reports as a fan), but
               it is drawn as the pump it is, not as a fan. Every other motor is
               the fan it was picked as — the drawing, never a 24 px glyph. -->
          <PumpAnimation v-if="pumpKind" class="fan-pin-art" :kind="pumpKind" :active="isOn" :speed="ringValue" />
          <FanAnimation
            v-else
            class="fan-pin-art"
            :kind="fanKind"
            :active="isOn"
            :style="isOn ? { '--fan-duration': fanSpinDuration } : {}" />
        </div>
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Button mode ───────────────────────────────────────────────────── -->
  <!-- The tile wears the family's head — the fan in its circle, spinning at the
       speed it reports, then the name over its own state — with the speed
       control under it: a chip per speed, or a bar for a continuous fan. -->
  <template v-else-if="mode === 'card'">
    <!-- Card face: the fan's own glyph (spinning while it runs), its state, and
         the speed control at the largest size. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="isOn"
      accent="rgb(56, 189, 248)"
      class="fan-card-face"
      @click="handleToggle"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <DeviceThumbnail
          v-if="device.thumbnailUrl || deviceThumbnailS3Key(device)"
          :url="device.thumbnailUrl"
          :s3-key="deviceThumbnailS3Key(device)"
          :alt="device.deviceName"
          class="fan-card-face__img" />
        <PumpAnimation
          v-else-if="pumpKind"
          class="fan-card-face__figure"
          :kind="pumpKind"
          :active="isOn"
          :speed="ringValue" />
        <FanAnimation
          v-else
          class="fan-card-face__figure"
          :kind="fanKind"
          :active="isOn"
          :style="isOn ? { '--fan-duration': fanSpinDuration } : {}" />
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="fan-card-face__value">{{ statusLabel }}</span>
      </template>

      <template v-if="cardSize === 'max'" #control>
        <div
          v-if="fanMode === 'speed_enum'"
          class="fan-speed-chip-row fan-card-face__chips"
          :style="{ gridTemplateColumns: `repeat(${speedOptions.length}, minmax(0, 1fr))` }">
          <div
            v-for="opt in speedOptions"
            :key="opt.value"
            class="fan-speed-chip"
            :class="{ 'fan-speed-chip--active': currentSpeedValue === opt.value }"
            :style="fanChipStyle(opt)"
            :title="opt.label"
            @click.stop="handleSetSpeed(opt.value)">
            <span class="fan-speed-chip__label">{{ compactSpeedLabel(opt) }}</span>
          </div>
        </div>
        <div
          v-else-if="fanMode === 'continuous'"
          class="fan-speed-bar fan-card-face__bar"
          :title="`${speedPercent}%`"
          @pointerdown.stop="onSpeedBarDown"
          @pointermove.stop="onSpeedBarMove"
          @pointerup.stop="onSpeedBarUp"
          @pointercancel.stop="onSpeedBarUp">
          <div class="fan-speed-bar__fill" :style="{ width: `${speedPercent}%` }"></div>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'mode_fan'"
      :name="device.deviceName"
      :is-on="isOn"
      active-color="sky-400"
      @toggle="handleToggle"
      @long-press="emit('open-control-modal')">
      <!-- The cell's one glyph, when this motor has a drawing to spare it. -->
      <template #art>
        <PumpAnimation v-if="pumpKind" :kind="pumpKind" :active="isOn" :speed="ringValue" />
        <FanAnimation
          v-else
          :kind="fanKind"
          :active="isOn"
          :style="isOn ? { '--fan-duration': fanSpinDuration } : {}" />
      </template>
    </DeviceGridCell>
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the hero and the rows (no head, no outer padding — the host's body
         container carries it). The fan's own colour rides down while it runs;
         otherwise the host's accent stands. -->
    <div class="fan-sheet" :style="isOn ? { '--sheet-accent': 'var(--device-color-fan)' } : {}">
      <!-- HERO — the fan's own drawing (the blades turn while it runs), whichever
           kind it is and whatever controls it has: the drawing IS the kind the
           owner picked, so an on/off fan shows its ceiling fan too. The status
           line reads under it. A continuous fan keeps its own power control beside
           the drawing (the toggle the old body had) on the same write path. -->
      <div class="fan-hero">
        <div
          class="fan-hero-figure"
          :class="{ 'fan-hero-figure--active': isOn }"
          :style="isOn ? { '--fan-duration': fanSpinDuration } : {}">
          <PumpAnimation v-if="pumpKind" :kind="pumpKind" :active="isOn" :speed="ringValue" />
          <FanAnimation v-else :kind="fanKind" :active="isOn" />
        </div>
        <button
          v-if="fanMode !== 'speed_enum'"
          type="button"
          class="fan-power"
          :class="{ 'fan-power--on': isOn, 'fan-power--busy': controlling }"
          :disabled="controlling"
          :aria-label="isOn ? tt('Turn off', 'Izklopi') : tt('Turn on', 'Vklopi')"
          @click="handleToggle">
          <span v-if="controlling" class="fan-power-spin" aria-hidden="true"></span>
          <ControlIcon v-else name="power" size="2.25rem" />
        </button>
        <div class="fan-hero-caption">{{ statusLabel }}</div>
      </div>

      <!-- SPEEDS — one chip per speed the device reports (Off / Low / High…),
           the widget's own enum options and its own write path. -->
      <div v-if="fanMode === 'speed_enum'" class="fan-chips">
        <ControlChip
          v-for="opt in speedOptions"
          :key="opt.value"
          :active="currentSpeedValue === opt.value"
          :disabled="controlling"
          @click="handleSetSpeed(opt.value)">
          {{ opt.label }}
        </ControlChip>
      </div>

      <!-- CONTINUOUS — the speed slider on the widget's own percentage write path. -->
      <div v-else-if="fanMode === 'continuous'" class="fan-speed">
        <div class="fan-row-head">
          <span class="fan-row-key">{{ tt('Speed', 'Hitrost') }}</span>
          <span class="fan-row-value">{{ speedPercent }}%</span>
        </div>
        <ControlSlider :model-value="speedPercent" :disabled="controlling" @change="handleSpeedPercentChange" />
      </div>

      <!-- Oscillation — only where the device reports it. -->
      <ControlToggleRow
        v-if="hasOscillation"
        :model-value="oscillationOn"
        :label="tt('Oscillation', 'Nihanje')"
        icon="wind"
        :disabled="controlling"
        @update:model-value="handleOscillationToggle" />

      <!-- Automation panel — the fan's own (temp controller + presence timer).
           A pump is not a fan: those automations are for moving air, and a pump
           has none of them, so the sheet leaves the panel out. -->
      <FanAutomationPanel v-if="!pumpKind" :device-id="device.id" />

      <p v-if="lastUpdated" class="fan-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { formatTime } from '../../utils/formatDate';
import DeviceThumbnail from '../device-buttons/DeviceThumbnail.vue';
import {
  deviceThumbnailS3Key,
  fanKindOf,
  isPumpDevice,
  pumpKindOf,
  type FanKind,
  type PumpKind,
} from './deviceWidgetTypes';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceCardShell from './DeviceCardShell.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import FanAutomationPanel from './fan/FanAutomationPanel.vue';
import FanAnimation from './fan/FanAnimation.vue';
import PumpAnimation from './pump/PumpAnimation.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlSlider from '../device-sheet/ControlSlider.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useDeviceValueOptionsStore } from '../../stores/deviceValueOptions';
import { tt } from '../../utils/consoleText';
import type { Device } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();
const fetchedEnumOptions = ref<Array<{ value: number; label: string }> | null>(null);
const speedBarDragging = ref(false);
let speedPercentDebounceTimer: number | null = null;

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

// ── Fan mode detection (based on which values exist on the device) ───────
const fanMode = computed<'on_off' | 'speed_enum' | 'continuous'>(() => {
  if ('fan_speed' in currentValues.value) return 'speed_enum';
  if ('fan_speed_percentage' in currentValues.value) return 'continuous';
  return 'on_off';
});

// ── Power state ──────────────────────────────────────────────────────────
const isOn = computed(() => {
  if (fanMode.value === 'speed_enum') {
    // Speed-based: on if fan_speed > 0
    const v = currentValues.value['fan_speed'];
    return v !== undefined && v !== null && Number(v) > 0;
  }
  // on_off / continuous: look for switch
  const key = ['switch', 'power', 'on_off', 'state'].find((k) => k in currentValues.value);
  if (key) {
    const v = currentValues.value[key];
    return v === true || v === 1 || v === 'on';
  }
  const dv = props.device.deviceValues?.find((v) => ['switch', 'power', 'on_off', 'state'].includes(v.valueKey));
  return dv?.currentValue === true || dv?.currentValue === 1 || dv?.currentValue === 'on';
});

/**
 * The pump drawing this tile shows — `null` for every other motor, so a fan
 * keeps its blades. The hub's Pump class is the master switch; the kind itself
 * is the `pump_type` setting, then the icon a pump saved before it still carries
 * and its name.
 */
const pumpKind = computed<PumpKind | null>(() =>
  isPumpDevice(props.device)
    ? pumpKindOf({
        icon: props.device.icon,
        deviceName: props.device.deviceName,
        shortName: props.device.shortName,
        deviceType: props.device.deviceType,
        controlType: props.device.deviceControl || props.device.controlType,
        settings: props.device.settings,
      })
    : null,
);

/**
 * The fan drawing this tile shows — the `fan_style` setting, then the icon a fan
 * saved before it still carries and its name. A pump never reaches it: the
 * drawing above is drawn instead.
 */
const fanKind = computed<FanKind>(() =>
  fanKindOf({
    icon: props.device.icon,
    deviceName: props.device.deviceName,
    shortName: props.device.shortName,
    deviceType: props.device.deviceType,
    controlType: props.device.deviceControl || props.device.controlType,
    settings: props.device.settings,
  }),
);

// ── Speed enum (speed_bool / speed_int) ──────────────────────────────────
const currentSpeedValue = computed(() => {
  const v = currentValues.value['fan_speed'];
  return v !== undefined && v !== null ? Number(v) : 0;
});

const speedOptions = computed<Array<{ value: number; label: string }>>(() => {
  // Use API-fetched enum_options (most reliable)
  if (fetchedEnumOptions.value && fetchedEnumOptions.value.length > 0) {
    return fetchedEnumOptions.value;
  }

  const vals = props.device.deviceValues || [];

  // Derive from speed_N boolean values on the device (speed_bool mode)
  const speedKeys = vals
    .filter((v) => /^speed_\d+$/.test(v.valueKey))
    .map((v) => Number(v.valueKey.replace('speed_', '')))
    .sort((a: number, b: number) => a - b);

  if (speedKeys.length > 0) {
    return [
      { value: 0, label: tt('Off', 'Izklopljeno') },
      ...speedKeys.map((n) => ({ value: n, label: tt(`Speed ${n}`, `Hitrost ${n}`) })),
    ];
  }

  // Fallback: Off + 3 speeds
  return [
    { value: 0, label: tt('Off', 'Izklopljeno') },
    { value: 1, label: tt('Speed 1', 'Hitrost 1') },
    { value: 2, label: tt('Speed 2', 'Hitrost 2') },
    { value: 3, label: tt('Speed 3', 'Hitrost 3') },
  ];
});

const currentSpeedLabel = computed(() => {
  const opt = speedOptions.value.find((o) => o.value === currentSpeedValue.value);
  return opt?.label ?? 'Off';
});

// ── Continuous speed (percentage) ────────────────────────────────────────
const speedPercent = computed(() => {
  const val = currentValues.value['fan_speed_percentage'];
  return typeof val === 'number' ? Math.round(val) : 0;
});

// ── Oscillation ──────────────────────────────────────────────────────────
const hasOscillation = computed(() => 'oscillation' in currentValues.value);
const oscillationOn = computed(() => {
  const v = currentValues.value['oscillation'];
  return v === true || v === 1;
});

// ── Status label (for button mode) ──────────────────────────────────────
const statusLabel = computed(() => {
  if (!isOn.value) return tt('Off', 'Izklopljeno');
  if (fanMode.value === 'speed_enum') return currentSpeedLabel.value;
  if (fanMode.value === 'continuous' && speedPercent.value > 0) return `${speedPercent.value}%`;
  return tt('On', 'Vklopljeno');
});

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// ── Actions ──────────────────────────────────────────────────────────────
async function handleToggle() {
  if (fanMode.value === 'speed_enum') {
    // Toggle: if on → off (0), if off → speed 1
    await setProperty(props.device, 'fan_speed', isOn.value ? 0 : 1);
  } else {
    await setProperty(props.device, 'switch', !isOn.value);
  }
}

async function handleSetSpeed(speed: number) {
  await setProperty(props.device, 'fan_speed', speed);
}

function queueSpeedPercent(value: number) {
  if (speedPercentDebounceTimer) clearTimeout(speedPercentDebounceTimer);
  speedPercentDebounceTimer = window.setTimeout(() => {
    setProperty(props.device, 'fan_speed_percentage', value);
  }, 200);
}

function speedPercentFromBarEvent(e: PointerEvent) {
  const el = e.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  return Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
}

function onSpeedBarDown(e: PointerEvent) {
  if (controlling.value) return;
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  speedBarDragging.value = true;
  queueSpeedPercent(speedPercentFromBarEvent(e));
}

function onSpeedBarMove(e: PointerEvent) {
  if (!speedBarDragging.value || controlling.value) return;
  queueSpeedPercent(speedPercentFromBarEvent(e));
}

function onSpeedBarUp() {
  speedBarDragging.value = false;
}

/**
 * The continuous fan's sheet slider — the released value, clamped, on the same
 * `fan_speed_percentage` write path the old range input used (it wrote on
 * change, and `ControlSlider`'s `change` fires on release).
 */
async function handleSpeedPercentChange(value: number) {
  if (!Number.isFinite(value)) return;
  await setProperty(props.device, 'fan_speed_percentage', Math.max(0, Math.min(100, Math.round(value))));
}

async function handleOscillationToggle() {
  await setProperty(props.device, 'oscillation', !oscillationOn.value);
}
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/**
 * The pin's ring (P13): the fan's own level — the reported percentage when it
 * has one, the reached step of its speed family, and all the way up for a plain
 * on/off fan (the same rule the light pin uses for a lamp with no dimming).
 */
const ringValue = computed(() => {
  if (!isOn.value) return 0;
  if (fanMode.value === 'speed_enum') {
    const max = speedOptions.value.length > 1 ? speedOptions.value[speedOptions.value.length - 1].value : 0;
    return max > 0 ? Math.round((currentSpeedValue.value / max) * 100) : 100;
  }
  if (fanMode.value === 'continuous') return speedPercent.value > 0 ? speedPercent.value : 100;
  return 100;
});

const fanSpinDuration = computed(() => {
  let duration = 1.5;
  if (fanMode.value === 'speed_enum' && currentSpeedValue.value > 0) {
    const maxSpeed = speedOptions.value.length > 1 ? speedOptions.value[speedOptions.value.length - 1].value : 3;
    const ratio = currentSpeedValue.value / maxSpeed;
    duration = 3 - ratio * 2.5; // 3s at min → 0.5s at max
  } else if (fanMode.value === 'continuous' && speedPercent.value > 0) {
    const ratio = speedPercent.value / 100;
    duration = 3 - ratio * 2.5; // 3s at 1% → 0.5s at 100%
  }
  return `${duration.toFixed(2)}s`;
});

onMounted(async () => {
  // Fetch enum_options for fan_speed value (not stored in DB, served by device class)
  const fanSpeedDv = props.device.deviceValues?.find((v) => v.valueKey === 'fan_speed');
  if (fanSpeedDv?.id) {
    try {
      const opts = await deviceValueOptionsStore.fetchOptions(fanSpeedDv.id);
      if (opts?.enum_options?.length) {
        fetchedEnumOptions.value = opts.enum_options.map((o) => ({
          value: Number(o.value),
          label: String(o.label),
        }));
      }
    } catch {
      // Fallback to derived options
    }
  }
});

function compactSpeedLabel(option: { value: number; label: string }) {
  if (option.value === 0) return tt('Off', 'Izklopljeno');
  if (/^speed\s+\d+$/i.test(option.label)) return String(option.value);
  return option.label.length <= 5 ? option.label : String(option.value);
}

function fanChipStyle(option: { value: number; label: string }) {
  const active = currentSpeedValue.value === option.value;

  if (!active) return undefined;

  if (option.value === 0) {
    return {
      backgroundColor: 'rgba(203, 213, 225, 0.96)',
      borderColor: 'rgba(148, 163, 184, 0.95)',
      color: 'rgb(15, 23, 42)',
    };
  }

  return {
    backgroundColor: 'rgba(56, 189, 248, 0.9)',
    borderColor: 'rgba(14, 165, 233, 0.95)',
    color: 'rgb(15, 23, 42)',
  };
}
</script>

<style scoped>
/* ── Button mode: the family's head, then the fan's own speed row ───────── */
/* One `color` while the fan runs drives the tile's border and glow, the glyph,
   the name and the state — the tiles' own state system. */
.fan-on {
  color: var(--device-color-fan, rgb(56 189 248));
  border-color: color-mix(in srgb, currentColor 55%, transparent) !important;
  box-shadow:
    0 0 10px color-mix(in srgb, currentColor 22%, transparent),
    0 0 26px color-mix(in srgb, currentColor 8%, transparent);
}
.fan-on .device-name,
.fan-on .fan-current-value {
  color: currentColor !important;
}

.fan-button-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  margin-top: 0;
}

.device-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}

.device-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}

/* The name stays small; the state under it carries the weight */
.fan-button-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}

.fan-current-value {
  font-size: var(--tile-status-size); /* the tiles' value line */
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

/* The icon circle rides the shared opaque, page-coloured fill. */
.device-icon--circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

/* Device photo fills the icon circle in the head row */
.device-icon--circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}

.fan-speed-chip-row {
  display: grid;
  gap: 0.25em;
  margin-top: 0.5em;
}

.fan-speed-chip {
  min-width: 0;
  height: 1.7em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5em;
  padding: 0 0.2em;
  font-weight: 700;
  text-align: center;
  color: rgb(107 114 128);
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.18);
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.fan-speed-chip__label {
  font-size: 0.55em;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fan-speed-chip--active {
  transform: translateY(-1px);
  box-shadow: 0 2px 10px rgba(14, 165, 233, 0.22);
}

/* A continuous fan gets the tiles' trough instead of chips */
.fan-speed-bar {
  position: relative;
  width: 100%;
  height: 1.35em;
  margin-top: 0.5em;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.18);
  cursor: pointer;
  touch-action: none;
}

.fan-speed-bar__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--device-color-fan, rgb(56 189 248));
  transition: width 0.15s ease-out;
}

/* ── Control mode: the sheet body ────────────────────────────────────────
   The fan drawing hero (or the plain power dial on an on/off fan), the speed
   chips / slider, the oscillation row and the meta line. Every accent reads
   `--sheet-accent`, which the body root hands down while the fan runs;
   otherwise the host's accent stands. */
.fan-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

.fan-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.fan-hero-figure {
  width: 7.5rem;
  height: 7.5rem;
  color: rgb(148, 163, 184);
  transition: color 0.3s ease;
}

.dark .fan-hero-figure {
  color: rgb(100, 116, 139);
}

.fan-hero-figure--active {
  color: var(--device-color-fan, rgb(56, 189, 248));
}

.fan-hero-caption {
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  text-align: center;
}

.dark .fan-hero-caption {
  color: rgb(var(--gray-400));
}

/* The one big control on an on/off or continuous fan: a 6rem circle, tinted
   by the sheet's accent while the fan is on; the busy state calms it. */
.fan-power {
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background 0.2s,
    box-shadow 0.2s,
    transform 0.15s;
}

.dark .fan-power {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-400));
}

.fan-power:hover:not(:disabled) {
  transform: translateY(-1px);
}

.fan-power--on {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 13%, transparent);
  color: var(--sheet-accent);
  box-shadow: 0 0 24px color-mix(in srgb, var(--sheet-accent) 22%, transparent);
}

.fan-power--busy {
  opacity: 0.7;
}

.fan-power:disabled {
  cursor: default;
}

.fan-power-spin {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--sheet-accent) 35%, transparent);
  border-top-color: var(--sheet-accent);
  animation: fan-sheet-spin 0.8s linear infinite;
}

@keyframes fan-sheet-spin {
  to {
    transform: rotate(360deg);
  }
}

/* One chip per speed the device offers (Off / Low / High…). */
.fan-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* A continuous fan's speed slider with its own label row. */
.fan-speed {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.fan-row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.fan-row-key {
  font-size: 0.8125rem;
  color: rgb(var(--gray-600));
}

.dark .fan-row-key {
  color: rgb(var(--gray-300));
}

.fan-row-value {
  font-size: 0.9375rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--gray-900));
}

.dark .fan-row-value {
  color: #fff;
}

/* The meta line: the clock icon and the last-updated stamp, centred. */
.fan-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  margin: 0;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .fan-meta {
  color: rgb(var(--gray-400));
}

@media (prefers-reduced-motion: reduce) {
  .fan-power-spin {
    animation: none !important;
  }
}

.device-icon-pin__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  /* A definite box, so a percentage-sized drawing inside it resolves. */
  width: 100%;
  height: 100%;
  color: rgba(200, 200, 200, 0.9);
  transition: color 0.3s ease;
}
.device-icon-pin__icon--active {
  color: var(--device-color-fan, rgb(56 189 248));
}

/* The pump's drawing sits in the ring's clear circle, at the size the fan glyph
   takes there — a percentage so it follows every pin size (sm/md/lg/fluid). */
.fan-pin-art {
  width: 62%;
  height: 62%;
}

/* The app theme is the `.dark` class, not the OS preference. */
.dark .fan-speed-chip {
  color: rgb(209 213 219);
  background: rgba(75, 85, 99, 0.35);
  border-color: rgba(107, 114, 128, 0.3);
}

.dark .fan-speed-bar {
  background: rgba(255, 255, 255, 0.06);
}

/* ── Card face ─────────────────────────────────────────────────────────────── */
.fan-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* A pump's drawing fills the card's media box the way a photo does */
.fan-card-face__figure {
  width: 100%;
  height: 100%;
}

.fan-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  color: #64748b;
}

.dark .fan-card-face__value {
  color: #94a3b8;
}

.fan-card-face__chips,
.fan-card-face__bar {
  width: 100%;
}
</style>
